import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Button, ButtonGroup, Grid, Typography, Box } from "@mui/material";
import { inspectionInfoStorage, SelectedView } from "../../services/ocr/InspectionInfoStorageModel";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { translationService } from "../../services/translation/TranslationService";
import PipeDiagram from "./PipeDiagram";
import { OcrService } from "../../services/ocr/OcrService";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { mainCodeColorStorage } from "../../services/mc-colors/MainCodeColorStorage";
import './Inspection.scss';

interface IInspectionTableProps {
  inspectionInsightStorage: typeof inspectionInfoStorage;
  ocrService: OcrService;
};

export const InspectionTable = observer(
  ({ inspectionInsightStorage, ocrService }: IInspectionTableProps) => {
    const distance = inspectionInsightStorage.detections.map((d) => d.distance);

    const pipeLength = ocrService.mainInformation.find(
      (item: { key: string }) => item.key.toLowerCase() === "rohrlänge" || item.key.toLowerCase() === "rohrlange"
    );
    const pipeLengthValue = pipeLength ? parseFloat(pipeLength.value) : 60;
    const mainCodes = inspectionInsightStorage.detections.map((d) => d.main_Code);
    const mainCodeColors = mainCodeColorStorage.colors; 

    const customOverlay = () => {
      return (
        <GridOverlay>
          <Typography variant="h6">
            {translationService.translate("ocrService.inspectionTable.noDetections")}
          </Typography>
        </GridOverlay>
      );
    };

    const getRowClassName = useCallback((id: number) => {
      var className = "row-" + id;
      if (id % 2 == 0) className += " row-even"
      else className += " row-odd"
      return className;
    }, [])

    return (
      <div className="base-data-table">
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ textAlign: "left", display: "block" }}>
            {translationService.translate("ocrService.inspectionInfo.table-name")}
          </Typography>
          <ButtonGroup variant="outlined" data-html2canvas-ignore>
            <Button
              onClick={() => inspectionInsightStorage.setView(SelectedView.Detected)}
              variant={
                inspectionInsightStorage.view === SelectedView.Detected ? "contained" : "outlined"
              }
            >
              {translationService.translate("ocrService.inspectionInfo.button-detectedText")}
            </Button>
            <Button
              onClick={() => inspectionInsightStorage.setView(SelectedView.Extracted)}
              variant={
                inspectionInsightStorage.view === SelectedView.Extracted ? "contained" : "outlined"
              }
            >
              {translationService.translate("ocrService.inspectionInfo.button-extractedInsights")}
            </Button>
            <Button
              onClick={() => inspectionInsightStorage.setView(SelectedView.Combined)}
              variant={
                inspectionInsightStorage.view === SelectedView.Combined ? "contained" : "outlined"
              }
            >
              {translationService.translate("ocrService.inspectionInfo.button-showBoth")}
            </Button>
          </ButtonGroup>
        </Box>

        <Grid
          container
          direction="row"
          spacing={0}
          sx={{ position: "relative", overflow: "hidden", flexWrap: "nowrap", height: "100%" }}
        >
          <Grid item sm="auto" sx={{ position: "sticky", left: 0, zIndex: 1 }}>
            <PipeDiagram
              distance={distance}
              pipeLengthValue={pipeLengthValue ?? 0}
              view={inspectionInsightStorage.view}
              selectedIndex={inspectionInsightStorage.selectedIndex}
              mainCodes={mainCodes}
              mainCodeColors={mainCodeColors}
            />
          </Grid>

          <div style={{ display: 'flex', flexGrow: 1,  overflow: 'auto', height: '100%' }}>
          <DataGridPro
            rows={inspectionInsightStorage.view === SelectedView.Combined ? inspectionInsightStorage.combinedRows : inspectionInsightStorage.rows}
            columns={inspectionInsightStorage.columns}
            columnHeaderHeight={40}
            rowHeight={inspectionInsightStorage.view === SelectedView.Combined ? undefined : 64}
            autoHeight
            columnGroupingModel={inspectionInsightStorage.view === SelectedView.Combined ? inspectionInsightStorage.combinedColumnGroup : []}
            hideFooter
            getRowId={(row: any) => row.id}
            getRowClassName={inspectionInsightStorage.view === SelectedView.Combined ?
              (params) => getRowClassName(params.indexRelativeToCurrentPage) : (params) => `row-${params.indexRelativeToCurrentPage}`}
            sx={{
              '& .MuiDataGrid-cell': {
                borderTop: inspectionInsightStorage.view === SelectedView.Combined ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                overflow: "visible",
                lineHeight: "normal"
              }
            }}
          />
          </div>
        </Grid>
      </div>
    );
  }
);
